import {
  Button,
  FormProvider,
  Grid,
  Stack,
  SelectField,
  GridItem,
  Center,
  FiPlus,
  Alert,
  ReadOnlyField,
} from '@agrotoken-tech/ui';
import { useTranslation } from '@bcba/react-hooks';
import {
  explorerBaseDefaultAdditionalDocumentForTaxInfo,
  jurisdictionOptions,
} from './utils';
import useTaxInfoForm, { TaxInfoProps } from './hooks/useTaxInfoForm';
import { LocaleKeys } from '@bcba/translations';
import { DocumentSection } from '../../../shared/DocumentSection';

export const TaxInfoForm = ({ data, refetch }: TaxInfoProps) => {
  const { t } = useTranslation();
  const {
    ADDITIONAL_DOCUMENTS,
    formMethods,
    isPendingChangeRequest,
    validationMessage,
    isFormReadOnly,
    ivaConditionReadOnlyValue,
    ivaConditionOptions,
    bruteIncomeOptions,
    jurisdictionsReadOnlyValuesFormatted,
    actionButtons,
    appendAdditionalDocument,
    removeAdditionalDocument,
    additionalDocumentsFields,
    profitTypesOptions,
    profitsReadOnlyValue,
    bruteIncomeReadOnlyValue,
  } = useTaxInfoForm({ data, refetch });

  return (
    <form noValidate>
      <FormProvider {...formMethods}>
        <Stack gap={8}>
          {isPendingChangeRequest && (
            <Alert
              title={t('explorer.changeRequest.alert.title')}
              description={validationMessage}
              variant="error"
            />
          )}
          <Grid templateColumns={{ lg: 'repeat(2, 1fr)' }} gap={6}>
            <DocumentSection
              isDocumentRequired
              documentName="cuitDocument"
              label={t(
                'backoffice.pages.taxInformation.cuitCertificateDocument.label',
              )}
              hideDelete={isFormReadOnly}
              isReadonly={isFormReadOnly}
            />
            {isFormReadOnly ? (
              <ReadOnlyField
                label={t('backoffice.pages.taxInformation.IVACondition.label')}
                value={t(
                  `backoffice.pages.taxInformation.IVA_CONDITION.${ivaConditionReadOnlyValue}` as LocaleKeys,
                )}
              />
            ) : (
              <SelectField
                name={'ivaCondition'}
                label={t('backoffice.pages.taxInformation.IVACondition.label')}
                placeholder={t(
                  'backoffice.pages.taxInformation.IVACondition.placeholder',
                )}
                options={ivaConditionOptions}
                errorMessageFormatter={t}
                rhfOptions={{ required: true }}
              />
            )}

            <GridItem />

            <GridItem mt={6}>
              {isFormReadOnly ? (
                <ReadOnlyField
                  label={t('backoffice.pages.taxInformation.IBB.label')}
                  value={t(
                    `backoffice.pages.taxInformation.BRUTE_INCOME_TYPES.${bruteIncomeReadOnlyValue}` as LocaleKeys,
                  )}
                />
              ) : (
                <SelectField
                  name={'bruteIncome'}
                  label={t('backoffice.pages.taxInformation.IBB.label')}
                  placeholder={t(
                    'backoffice.pages.taxInformation.IBB.placeholder',
                  )}
                  options={bruteIncomeOptions}
                  errorMessageFormatter={t}
                  rhfOptions={{ required: true }}
                />
              )}
            </GridItem>

            <GridItem mt={6}>
              {isFormReadOnly ? (
                <ReadOnlyField
                  label={t(
                    'backoffice.pages.taxInformation.jurisdiction.label',
                  )}
                  value={jurisdictionsReadOnlyValuesFormatted}
                />
              ) : (
                <SelectField
                  name={'jurisdictions'}
                  label={t(
                    'backoffice.pages.taxInformation.jurisdiction.label',
                  )}
                  placeholder={t(
                    'backoffice.pages.taxInformation.jurisdiction.placeholder',
                  )}
                  options={jurisdictionOptions}
                  errorMessageFormatter={t}
                  isMulti
                />
              )}
            </GridItem>

            <DocumentSection
              documentName="iibbDocument"
              isDocumentRequired
              label={t('backoffice.pages.taxInformation.IBBCertificate.label')}
              hideDelete={isFormReadOnly}
              isReadonly={isFormReadOnly}
            />

            <GridItem mt={6}>
              {isFormReadOnly ? (
                <ReadOnlyField
                  value={t(
                    `backoffice.pages.taxInformation.PROFIT_TYPES.${profitsReadOnlyValue}` as LocaleKeys,
                  )}
                  label={t('backoffice.pages.taxInformation.profitsTax.label')}
                />
              ) : (
                <SelectField
                  name={'profits'}
                  label={t('backoffice.pages.taxInformation.profitsTax.label')}
                  placeholder={t(
                    'backoffice.pages.taxInformation.profitsTax.placeholder',
                  )}
                  options={profitTypesOptions}
                  errorMessageFormatter={t}
                  rhfOptions={{ required: true }}
                />
              )}
            </GridItem>

            <DocumentSection
              documentName="profitsDocument"
              isDocumentRequired
              label={t('backoffice.pages.taxInformation.profitsDocument.label')}
              hideDelete={isFormReadOnly}
              isReadonly={isFormReadOnly}
            />
            <DocumentSection
              mt={6}
              documentName="cm05Document"
              isDocumentRequired={false}
              label={t('backoffice.pages.taxInformation.CM05Certificate.label')}
              hideDelete={isFormReadOnly}
              isReadonly={isFormReadOnly}
            />
            <DocumentSection
              mt={6}
              isDocumentRequired={false}
              documentName="exemptionsDocument"
              label={t(
                'backoffice.pages.taxInformation.ExemptionsCertificate.label',
              )}
              hideDelete={isFormReadOnly}
              isReadonly={isFormReadOnly}
            />

            {additionalDocumentsFields.map((field, index) => {
              const fieldPrefix = `${ADDITIONAL_DOCUMENTS}.${index}` as const;
              return (
                <DocumentSection
                  isDocumentRequired={false}
                  key={field.id}
                  mt={6}
                  documentName={fieldPrefix}
                  handleRemove={() => removeAdditionalDocument(index)}
                  label={
                    t(
                      'backoffice.pages.taxInformation.additionalDocumentation.label',
                    ) + ` ${index + 1}`
                  }
                  hideDelete={isFormReadOnly}
                  isReadonly={isFormReadOnly}
                />
              );
            })}

            <GridItem colSpan={{ lg: 2 }}>
              <Center>
                <Button
                  leftIcon={<FiPlus />}
                  variant="linkPrimary"
                  size={'sm'}
                  isDisabled={isFormReadOnly}
                  onClick={() =>
                    appendAdditionalDocument(
                      explorerBaseDefaultAdditionalDocumentForTaxInfo as any, //TODO: check this type,
                    )
                  }
                >
                  {t(
                    'backoffice.pages.taxInformation.additionalDocumentation.add',
                  )}
                </Button>
              </Center>
            </GridItem>
          </Grid>
          {actionButtons}
        </Stack>
      </FormProvider>
    </form>
  );
};
